import { Fragment, useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { ConfigProvider, Tabs } from 'antd';
import { useTheme } from 'styled-components';
import { ReactComponent as IconExcelReport } from '../../assets/icons/icon-excel-report.svg';
import { Button, NavHeader } from '../../components';
import { BaseFilterLeft } from '../../components/BaseFilterLeft';
import { useApp, useAuth } from '../../context';
import { useSolicitation } from '../../context/solicitation';
import { Body, Container, Content } from './styles';
import { CardDemandProposalDetails } from '../../components/Cards/CardDemandProposalDetails';
import { CardDemandDetails } from '../../components/Cards/CardDemandDetails';
import { useReport } from '../../context/report';
import { useAlert } from '../../hooks/useAlert';
import TitlePage from '../../components/TitlePage';
import JoyrideTour from '../../components/JoyrideTour';
import { demandProposalMock } from '../../constants/Tour/Mocks/demandMock';
import { demandSteps } from '../../constants/Tour/Steps/demandSteps';
import { TutorialType } from '../../types/enums';

export function YourDemands() {
  const { user } = useAuth();
  const { isGeneratingReport, handleGenerateDemandReport } = useReport();
  const alert = useAlert();
  const app = useApp();
  const { colors } = useTheme();
  const [runningTutorial, setRunningTutorial] = useState(false);
  const [activeTab, setActiveTab] = useState<'demands' | 'proposals'>('demands');

  const {
    demands,
    setDemands,
    demandProposals,
    setDemandProposals,
    setFilter,
    filter,
    isLoadingDemandProposals,
    isLoadingDemands,
    isFetchingNextPageDemands,
    hasNextPageDemands,
    isFetchingNextPageDemandProposals,
    hasNextPageDemandProposals,
    fetchNextPageDemands,
    fetchNextPageDemandProposals,
  } = useSolicitation();

  const handleGenerateReport = () => {
    if (isGeneratingReport) return;
    alert.show({ message: 'Gerando relatório... Aguarde', type: 'success' });
    handleGenerateDemandReport({ ...filter });
  };

  useEffect(() => {
    app.setLoading(isLoadingDemandProposals || isLoadingDemands);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingDemandProposals, isLoadingDemands]);

  useEffect(() => {
    setDemands(
      demands.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    );
    setDemandProposals(
      demandProposals.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    );
  }, [demandProposals, demands, setDemandProposals, setDemands]);

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.YOUR_DEMANDS)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);

  const handleTabChange = (key: string) => {
    setActiveTab(key === 'proposals' ? 'proposals' : 'demands');
  };
  return (
    <>
      <JoyrideTour
        type={TutorialType.YOUR_DEMANDS}
        steps={demandSteps}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <NavHeader typeHeader="company" />
      <Body>
        <BaseFilterLeft
          onChangeFilter={(solicitationFilter) => {
            setFilter(solicitationFilter);
          }}
          displayOptions={{
            client: true,
            processNumber: true,
            correspondent: false,
            correspondenceType: true,
            correspondence: true,
            date: true,
            especilty: true,
            status: true,
            audienceType: true,
            folderName: true,
            partName: true,
            court: false,
            protocol: true,
            paymentStatus: true,
            district: true,
          }}
        />
        <Container>
          <div className="header">
            <TitlePage
              title="Suas Demandas"
              onRequestOpen={() => {
                setRunningTutorial(true);
              }}
              id="start-item"
            />
            {activeTab === 'demands' && (
              <Button
                id="excel-report"
                text="Extrair relatório"
                LeftComponent={IconExcelReport}
                styleLeftComponent={{ fill: colors.tertiary_500 }}
                style={{ fontSize: '1.20rem', display: 'flex', alignItems: 'center' }}
                dimensions={{
                  width: '10rem',
                  height: '1.25rem',
                }}
                positionLeftElement={5}
                disabled={isGeneratingReport}
                border={{ color: 'background_white', width: 0 }}
                color="tertiary_500"
                textHoverColor="tertiary_500"
                backgroundHoverColor="background_white"
                onClick={handleGenerateReport}
              />)}
          </div>
          <Content>
            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: colors.primary,
                },
              }}>
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={[
                  {
                    key: 'demands',
                    label: `Demandas Aceitas (${demands?.length || 0})`,
                    children: (
                      <>
                        {runningTutorial ? (
                          <div id="demands">
                            <CardDemandProposalDetails demandProposal={demandProposalMock} isTutorial />
                          </div>
                        ) : demands?.length ? (
                          <Fragment key={demands.length}>
                            {demands.map((demand) => (
                              <CardDemandDetails key={demand.id} demandParam={demand} />
                            ))}
                          </Fragment>
                        ) : (
                          <Typography align="center" color={colors.secondary}>
                            Nenhuma demanda encontrada...
                          </Typography>
                        )}

                        {hasNextPageDemands && (
                          <Grid container justifyContent="center" padding="1rem 1rem">
                            <Button
                              text="Carregar mais..."
                              onClick={fetchNextPageDemands}
                              dimensions={{ height: '2rem', width: '10rem' }}
                              color="primary"
                              textHoverColor="tertiary_500"
                              backgroundHoverColor="transparent"
                              background="transparent"
                              border={{ color: 'transparent' }}
                              loading={isLoadingDemands || isFetchingNextPageDemands}
                              disabled={isLoadingDemands || isFetchingNextPageDemands}
                            />
                          </Grid>
                        )}
                      </>
                    ),
                  },
                  {
                    key: 'proposals',
                    label: `Demandas Propostas (${demandProposals?.length || 0})`,
                    children: (
                      <>
                        {demandProposals?.length ? (
                          <Fragment key={demandProposals.length}>
                            {demandProposals.map((proposal) => (
                              <CardDemandProposalDetails
                                key={proposal.solicitationId}
                                demandProposal={proposal}
                              />
                            ))}
                          </Fragment>
                        ) : (
                          <Typography align="center" color={colors.secondary}>
                            Nenhuma proposta encontrada...
                          </Typography>
                        )}

                        {hasNextPageDemandProposals && (
                          <Grid container justifyContent="center" padding="1rem 1rem">
                            <Button
                              text="Carregar mais..."
                              onClick={fetchNextPageDemandProposals}
                              dimensions={{ height: '2rem', width: '10rem' }}
                              color="primary"
                              textHoverColor="tertiary_500"
                              backgroundHoverColor="transparent"
                              background="transparent"
                              border={{ color: 'transparent' }}
                              loading={isLoadingDemandProposals || isFetchingNextPageDemandProposals}
                              disabled={isLoadingDemandProposals || isFetchingNextPageDemandProposals}
                            />
                          </Grid>
                        )}
                      </>
                    ),
                  },
                ]}
              />
            </ConfigProvider>
          </Content>
        </Container>
      </Body>
    </>
  );
}
